import { createAsyncThunk } from '@reduxjs/toolkit';
import get_cost_matrix from "functions/server/cost_matrix.js";
import process_uploaded_travel_costs from 'functions/server/process_uploaded_travel_costs.js';
import ProcessUploadedTravelCostResponse from 'open-api-gen/model/ProcessUploadedTravelCostResponse.js';
import { fetchPlans } from 'store/reducers/thunks/planThunks';
import { selectRoadNetworkAccess } from 'store/functions/entitlements';
import { manualMultiplierNotice, markManualMultiplierNoticeShown, straightLineFallbackNotice } from 'store/functions/costMethods.js';
import { costsFilledBy } from 'store/functions/travelCostResults.js';
import { isCartesian } from 'store/functions/coordinateSystem.js';
// Used at call time only; inputReducer also imports this module.
import { costMatrixRequestMethods } from 'store/functions/openapi_conversion_functions';

export const fetchTravelCosts = createAsyncThunk('posts/fetchTravelCosts', async (payload, {getState, dispatch, fulfillWithValue}) => {

  // Which calculation methods this user may request depends on their plan (see
  // store/functions/costMethods). Load the plan catalog first if nothing has
  // tried to yet, so a free user isn't sent to the road network (and refused)
  // just because the catalog hadn't arrived. A failed load fails open.
  const catalogStatus = getState().ui?.planCatalog?.status;
  if (catalogStatus === 'idle' || catalogStatus === 'loading') {
    await dispatch(fetchPlans());
  }

  const state = getState();
  const data = state.inputData.present.data;
  if (!data['locations']) {
    throw new Error('Expecting location data in input data');
  }

  if (!data['travel_costs']) {
    throw new Error('Expecting travel cost data in input data');
  }
  
  const present = state.inputData.present;
  const roadNetworkAllowed = state.ui ? selectRoadNetworkAccess(state) : undefined;
  const response = await get_cost_matrix(present, { roadNetworkAllowed });

  // Which method each calculated field used, so the reducer can tag the rows and
  // a later "Calculate missing" can tell when it would mix methods.
  let costMethods;
  let straightLineFallback = null;
  let multipliersNotice = null;
  /*
  `cost` is tagged as well as distance and duration. The tag is what tells a
  later calculation which numbers the app produced and which a person typed or
  uploaded (countReplacedTravelCostPairs). The backend derives cost from the
  chosen metric, so it came from the same method; leaving it untagged made every
  freshly calculated row look hand-entered in the `cost` column.
  */
  if (isCartesian(present)) {
    const planar = present.generalSettings.costs.distanceMethod === 'manhattan' ? 'manhattan' : 'euclidean';
    costMethods = { distance: planar, duration: planar, cost: planar };
  } else {
    const costs = present.generalSettings.costs;
    // The methods the request used (travel time is always calculated; see
    // costMatrixRequestMethods).
    const request = costMatrixRequestMethods(present, roadNetworkAllowed);
    const { needDistance } = request;
    costMethods = {};
    if (needDistance) costMethods.distance = request.distanceMethod;
    costMethods.duration = request.durationMethod;
    // The metric's own method: cost is that quantity, scaled.
    const costSource = costs.costMetric === 'duration' ? costMethods.duration : costMethods.distance;
    if (costSource) costMethods.cost = costSource;
    // If a road-network method the person chose was replaced by straight-line,
    // the reducer tells them, once, for this calculation.
    straightLineFallback = straightLineFallbackNotice({
      costs,
      // The matrix as it stands, to see whether this scenario was calculated on
      // the road network before.
      rows: present.data.travel_costs,
      needDistance,
      // Only a travel-time method the scenario configures. An unconfigured one
      // follows the distance method, for every plan, so the plan isn't why it is
      // straight-line.
      needDuration: request.durationConfigured,
      roadNetworkAllowed,
      signedIn: Boolean(present.loginData && present.loginData.loggedIn),
      fromExample: Boolean(present.generalSettings.scenario && present.generalSettings.scenario.exampleSlug),
    });
    /*
    Costs entered by hand send a multiplier of 1 (multipliersOff). A scenario
    saved with other multipliers gets different costs than it used to, so it is
    told once, in this calculation's message, the first time it actually fills
    a cost here: a calculation that filled none (nothing came back, or every
    cost was already there) changes no cost and doesn't use the notice up.

    Keyed on the saved scenario's own id (generalSettings.scenario.scenarioId).
    The top-level generalSettings.scenarioId can carry over from an example
    session. An unsaved scenario has nothing stable to remember, so it is told
    each time.
    */
    const scenarioId = (present.generalSettings.scenario && present.generalSettings.scenario.scenarioId) || null;
    multipliersNotice = payload && payload.silent
      ? null
      : manualMultiplierNotice({ costs, vehicleTypes: present.data.vehicle_types, scenarioId });
    if (multipliersNotice) {
      const filled = costsFilledBy(present.data.travel_costs, (response && response.travelCostResponses) || [], {
        fillMissingOnly: Boolean(payload && payload.fillMissingOnly),
        costs,
        vehicleTypes: present.data.vehicle_types,
      });
      if (filled) markManualMultiplierNoticeShown(scenarioId);
      else multipliersNotice = null;
    }
  }
  return fulfillWithValue(response, { costMethods, straightLineFallback, multipliersNotice });
});

/**
 * This function takes an uploaded travel cost file and fills out the cost table. It will need to match the columns
 * from the new file with the current table; and match location names.
 */
export const decipherUploadedTravelCosts = createAsyncThunk('posts/decipherUploadedTravelCosts', async (payload) => {
  const uploadedData = payload.uploadedData;
  const overwriteExisting = true; //payload.overwriteExisting

  const response = await process_uploaded_travel_costs({uploadedData, overwriteExisting})

  const parsedResponse = ProcessUploadedTravelCostResponse.constructFromObject(JSON.parse(response));
  return parsedResponse

});
